/**
 * Discord helpers that run on the server. They take `fetch` as a parameter so they can be tested
 * without the network. Nothing here stores tokens; they are used once and dropped.
 */

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const API = "https://discord.com/api/v10";
export const AVATAR_PREFIX = "https://cdn.discordapp.com/";

export interface VerifiedUser {
  id: string;
  name: string;
  /** Discord CDN picture, or undefined when the player never set one (the table shows their initial instead). */
  avatar?: string;
}

/** Swap the one-time `code` from the Activity for an access token (needs the app's client secret). */
export async function exchangeCode(
  code: string,
  clientId: string,
  clientSecret: string,
  fetchImpl: FetchLike = fetch,
): Promise<string | null> {
  const res = await fetchImpl(`${API}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: clientId,
      client_secret: clientSecret,
      grant_type: "authorization_code",
      code,
    }).toString(),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { access_token?: unknown };
  return typeof data.access_token === "string" ? data.access_token : null;
}

/**
 * Ask Discord who this token belongs to. Also checks the token was issued to OUR app, so a token
 * minted for some other app can't be used to sit at our table.
 */
export async function verifyToken(
  token: string,
  clientId: string,
  fetchImpl: FetchLike = fetch,
): Promise<VerifiedUser | null> {
  let res: Response;
  try {
    res = await fetchImpl(`${API}/oauth2/@me`, { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const data = (await res.json()) as {
    application?: { id?: string };
    user?: { id?: string; username?: string; global_name?: string | null; avatar?: string | null };
  };
  const u = data.user;
  if (data.application?.id !== clientId || !u?.id || !/^\d{5,25}$/.test(u.id)) return null;
  return {
    id: u.id,
    name: (u.global_name || u.username || "Player").slice(0, 24),
    avatar: u.avatar ? avatarUrl(u.id, u.avatar) : undefined,
  };
}

/**
 * The player's own picture on Discord's CDN (an address Discord's Activity sandbox allows by default).
 * Discord's generated default avatars live at a path the sandbox does not allow, so we don't use them.
 */
export function avatarUrl(userId: string, hash: string): string | undefined {
  return /^[a-zA-Z0-9_]+$/.test(hash) ? `${AVATAR_PREFIX}avatars/${userId}/${hash}.png?size=128` : undefined;
}

/** Only ever show avatars that come from Discord's CDN. */
export function safeAvatar(url: unknown): string | undefined {
  return typeof url === "string" && url.startsWith(AVATAR_PREFIX) && url.length < 300 && !/["'<>\s]/.test(url)
    ? url
    : undefined;
}
